import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { RadioOption, GlobalRadioButtons } from '../../../components/GlobalComponents/GlobalRadioButtons';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { API_BASE_URL } from '../../../apiSetup/apiConfig';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+?\d{10,15}$/;
const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

interface TeacherFormData {
  name: string;
  gender: string;
  email_id: string;
  parentage: string;
  dob: Date | null;
  address: string;
  contact_no: string;
  account_no: string;
  bank_name: string;
  ifsc_code: string;
  user_login: string;
  user_pass: string;
}

interface RegisterTeacherFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (shouldResetPage?: boolean) => void;
  teacherData?: any | null;
}

const RegisterTeacherModal: React.FC<RegisterTeacherFormProps> = ({
  open,
  onClose,
  onSuccess,
  teacherData,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<TeacherFormData>({
    name: '',
    gender: '',
    email_id: '',
    parentage: '',
    dob: null,
    address: '',
    contact_no: '',
    account_no: '',
    bank_name: '',
    ifsc_code: '',
    user_login: '',
    user_pass: '',
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const genderOptions: RadioOption[] = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    if (teacherData) {
      setFormData({
        name: teacherData.name || '',
        gender: teacherData.gender || '',
        email_id: teacherData.email_id || '',
        parentage: teacherData.parentage || '',
        dob: teacherData.dob ? new Date(teacherData.dob) : null,
        address: teacherData.address || '',
        contact_no: teacherData.contact_no || '',
        account_no: teacherData.account_no || '',
        bank_name: teacherData.bank_name || '',
        ifsc_code: teacherData.ifsc_code || '',
        user_login: '',
        user_pass: '',
      });
      setProfilePicturePreview(
        teacherData.profile_picture_url
          ? `${teacherData.profile_picture_url}`
          : null
      );
      setProfilePicture(null);
    } else if (!teacherData && open) {
      resetForm();
    }
  }, [teacherData, open]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const resetForm = () => {
    setFormData({
      name: '',
      gender: '',
      email_id: '',
      parentage: '',
      dob: null,
      address: '',
      contact_no: '',
      account_no: '',
      bank_name: '',
      ifsc_code: '',
      user_login: '',
      user_pass: '',
    });
    setProfilePicture(null);
    setProfilePicturePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid File',
        detail: 'Only JPG, JPEG, PNG, and GIF files are allowed',
        life: 4000,
      });
      return;
    }
    if (file.size > 500 * 1024) {
      toast.current?.show({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'Profile picture must be under 500KB',
        life: 4000,
      });
      return;
    }

    setProfilePicture(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(
      teacherData?.profile_picture_url
        ? `${API_BASE_URL}${teacherData.profile_picture_url}`
        : null
    );
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateForm = () => {
    const requiredFields = teacherData
      ? ['name', 'gender', 'email_id', 'parentage', 'dob', 'address', 'contact_no']
      : ['name', 'gender', 'email_id', 'parentage', 'dob', 'address', 'contact_no', 'user_login', 'user_pass'];

    const missingFields = requiredFields.filter((field) => {
      const value = formData[field as keyof TeacherFormData];
      if (field === 'dob') return !value;
      return !value?.toString().trim();
    });

    if (missingFields.length > 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: `Missing required fields: ${missingFields.join(', ')}`,
        life: 5000,
      });
      return false;
    }
    if (!emailRegex.test(formData.email_id)) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Invalid email format', life: 5000 });
      return false;
    }
    if (!phoneRegex.test(formData.contact_no)) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Invalid contact number (10-15 digits)', life: 5000 });
      return false;
    }
    if (formData.ifsc_code && !ifscRegex.test(formData.ifsc_code.toUpperCase())) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Invalid IFSC code format (e.g., SBIN0001234)', life: 5000 });
      return false;
    }
    if (!teacherData && (!formData.user_pass || formData.user_pass.length < 8)) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Password must be at least 8 characters', life: 5000 });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let response;

      if (teacherData) {
        // Use multipart/form-data for edit (supports profile picture)
        const fd = new FormData();
        fd.append('teacher_id', teacherData.id);
        fd.append('name', formData.name);
        fd.append('gender', formData.gender);
        fd.append('email_id', formData.email_id);
        fd.append('parentage', formData.parentage);
        fd.append('dob', formData.dob ? formData.dob.toISOString().split('T')[0] : '');
        fd.append('address', formData.address);
        fd.append('contact_no', formData.contact_no);
        fd.append('account_no', formData.account_no);
        fd.append('bank_name', formData.bank_name.toUpperCase());
        fd.append('ifsc_code', formData.ifsc_code.toUpperCase());
        if (profilePicture) {
          fd.append('profile_picture', profilePicture);
        }

        response = await apiClient({
          method: 'POST',
          url: 'update-teacher-form',
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // JSON for new teacher registration (no file upload)
        response = await apiClient({
          method: 'POST',
          url: 'teachers',
          data: {
            name: formData.name,
            gender: formData.gender,
            email_id: formData.email_id,
            parentage: formData.parentage,
            dob: formData.dob ? formData.dob.toISOString().split('T')[0] : '',
            address: formData.address,
            contact_no: formData.contact_no,
            account_no: formData.account_no,
            bank_name: formData.bank_name.toUpperCase(),
            ifsc_code: formData.ifsc_code.toUpperCase(),
            user_login: formData.user_login,
            user_pass: formData.user_pass,
          },
        });
      }

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: teacherData ? 'Teacher updated successfully!' : 'Teacher registered successfully!',
          life: 4000,
        });
        setTimeout(() => {
          resetForm();
          onSuccess(!teacherData);
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || `Failed to ${teacherData ? 'update' : 'register'} teacher`,
          life: 5000,
        });
      }
    } catch (err: any) {
      const message = err.response?.data?.message || `Failed to ${teacherData ? 'update' : 'register'} teacher`;
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => {
    const hasProfilePicture = teacherData?.profile_picture_url;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
        <div
          style={{
            width: '84px', height: '84px', borderRadius: '14px',
            background: hasProfilePicture ? 'transparent' : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '24px', fontWeight: 700,
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)', flexShrink: 0,
            border: '3px solid white', overflow: 'hidden',
          }}
        >
          {hasProfilePicture ? (
            <img
              src={`${API_BASE_URL}${teacherData.profile_picture_url}`}
              alt={teacherData.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)';
                  target.parentElement.innerHTML = '<i class="pi pi-user-edit" style="font-size: 24px; color: white;"></i>';
                }
              }}
            />
          ) : (
            <i className={teacherData ? 'pi pi-user-edit' : 'pi pi-user-plus'}></i>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            {teacherData ? 'Edit Teacher Information' : 'Register New Teacher'}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {teacherData ? 'Update teacher details below' : 'Fill in the teacher information below'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; background-color:rgba(0,0,0,0); } to { opacity:1; background-color:rgba(0,0,0,0.25); } }
          @keyframes maskFadeOut { from { opacity:1; background-color:rgba(0,0,0,0.25); } to { opacity:0; background-color:rgba(0,0,0,0); } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Profile Picture Section (edit only) ── */}
          {teacherData && (
            <div style={{
              background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px',
              border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '16px', paddingBottom: '12px',
                borderBottom: '1px solid rgba(229,231,235,0.6)',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white',
                }}>
                  <i className="pi pi-camera" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Profile Picture
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {/* Avatar Preview */}
                <div style={{
                  width: '90px', height: '90px', borderRadius: '50%', overflow: 'hidden',
                  border: '3px solid rgba(59,130,246,0.3)', flexShrink: 0,
                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {profilePicturePreview ? (
                    <img src={profilePicturePreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <i className="pi pi-user" style={{ fontSize: '32px', color: '#6366f1' }}></i>
                  )}
                </div>

                {/* Upload Controls */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleProfilePictureChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #3b82f6',
                        background: 'white', color: '#3b82f6', fontSize: '13px', fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px', opacity: loading ? 0.6 : 1,
                      }}
                    >
                      <i className="pi pi-upload" style={{ fontSize: '12px' }}></i>
                      {profilePicturePreview ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {profilePicture && (
                      <button
                        onClick={handleRemoveProfilePicture}
                        disabled={loading}
                        style={{
                          padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #ef4444',
                          background: 'white', color: '#ef4444', fontSize: '13px', fontWeight: 600,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', gap: '6px', opacity: loading ? 0.6 : 1,
                        }}
                      >
                        <i className="pi pi-times" style={{ fontSize: '12px' }}></i>
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                    {profilePicture ? (
                      <span style={{ color: '#10b981', fontWeight: 600 }}>
                        <i className="pi pi-check-circle" style={{ marginRight: '4px' }}></i>
                        {profilePicture.name} ({(profilePicture.size / 1024).toFixed(1)} KB)
                      </span>
                    ) : (
                      'JPG, PNG or GIF · Max 500KB'
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Personal Information ── */}
          <div style={{
            background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px',
            border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(229,231,235,0.6)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '32px', height: '32px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white',
              }}>
                <i className="pi pi-user" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Personal Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalInputField label="Full Name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter teacher's full name" required disabled={loading} icon="pi pi-user" />

              {!teacherData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <GlobalInputField label="Username" value={formData.user_login}
                    onChange={(e) => setFormData({ ...formData, user_login: e.target.value })}
                    placeholder="Enter username" required disabled={loading} icon="pi pi-at" />
                  <GlobalInputField label="Password" type="password" value={formData.user_pass}
                    onChange={(e) => setFormData({ ...formData, user_pass: e.target.value })}
                    placeholder="Enter password (min 8 chars)" required disabled={loading} icon="pi pi-lock" />
                </div>
              )}

              <GlobalRadioButtons label="Gender" options={genderOptions} value={formData.gender}
                onChange={(value) => setFormData({ ...formData, gender: value })}
                required disabled={loading} layout="horizontal" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField label="Email" type="email" value={formData.email_id}
                  onChange={(e) => setFormData({ ...formData, email_id: e.target.value })}
                  placeholder="teacher@example.com" required disabled={loading} icon="pi pi-envelope" />
                <GlobalInputField label="Parentage" value={formData.parentage}
                  onChange={(e) => setFormData({ ...formData, parentage: e.target.value })}
                  placeholder="Father's/Guardian's name" required disabled={loading} icon="pi pi-users" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalDateField label="Date of Birth" value={formData.dob}
                  onChange={(date) => setFormData({ ...formData, dob: date as Date | null })}
                  placeholder="Select date" required disabled={loading}
                  dateFormat="dd/mm/yy" showButtonBar maxDate={new Date()} />
                <GlobalInputField label="Address" value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter residential address" required disabled={loading} icon="pi pi-map-marker" />
              </div>
            </div>
          </div>

          {/* ── Contact & Bank Information ── */}
          <div style={{
            background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px',
            border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(229,231,235,0.6)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '32px', height: '32px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white',
              }}>
                <i className="pi pi-phone" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Contact & Bank Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalInputField label="Contact Number" type="tel" value={formData.contact_no}
                onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                placeholder="Enter 10-15 digit number" required disabled={loading} icon="pi pi-phone" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField label="Account Number" value={formData.account_no}
                  onChange={(e) => setFormData({ ...formData, account_no: e.target.value })}
                  placeholder="Enter bank account number" disabled={loading} icon="pi pi-credit-card" />
                <GlobalInputField label="Bank Name" value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Enter bank name" disabled={loading} icon="pi pi-building" />
              </div>

              <GlobalInputField label="IFSC Code" value={formData.ifsc_code}
                onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                placeholder="e.g., SBIN0001234" disabled={loading} icon="pi pi-tag" />
            </div>
          </div>

          {/* ── Submit ── */}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <GlobalButton
              label={loading ? (teacherData ? 'Updating...' : 'Registering...') : (teacherData ? 'Update Teacher' : 'Register Teacher')}
              onClick={handleSubmit}
              variant="primary" size="medium"
              disabled={loading} loading={loading} fullWidth={false}
            />
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default RegisterTeacherModal;